import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { turni, persone, organizzazioni } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Clock, Plus, Users, AlertTriangle, CheckCircle, Calendar, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SETTORI = [
  "Accoglienza", "Carraia", "Comunicazioni", "Cucina", "Infermeria", "Logistica", "Magazzino", "Mensa", "Sicurezza", "Trasporti"
];

export default function Operazioni() {
  const [turnoDialogOpen, setTurnoDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [turnoForm, setTurnoForm] = useState({
    nome: "", orario_inizio: "08:00", orario_fine: "14:00",
    settore: "Cucina", volontari_richiesti: 2, data: selectedDate, note: "", organizzazioni_turno: []
  });

  const queryClient = useQueryClient();

  const { data: turniData } = useQuery({
    queryKey: ['turni', selectedDate],
    queryFn: () => turni.list({ data: selectedDate }),
  });

  const { data: personeData } = useQuery({
    queryKey: ['persone'],
    queryFn: () => persone.list({ limit: 200 }),
  });

  
  const { data: orgData } = useQuery({
    queryKey: ['organizzazioni'],
    queryFn: () => organizzazioni.list(),
  });
  const orgList = (orgData || []).slice().sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));
  const createTurnoMutation = useMutation({
    mutationFn: turni.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['turni']);
      setTurnoDialogOpen(false);
      setTurnoForm({ nome: "", orario_inizio: "08:00", orario_fine: "14:00", settore: "Cucina", volontari_richiesti: 2, data: selectedDate, note: "", organizzazioni_turno: [] });
      toast.success("Turno creato");
    },
    onError: (error) => toast.error(error.message),
  });

  const assegnaMutation = useMutation({
    mutationFn: ({ turnoId, personaId }) => turni.assegna(turnoId, personaId),
    onSuccess: () => {
      queryClient.invalidateQueries(['turni']);
      toast.success("Volontario assegnato");
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteTurnoMutation = useMutation({
    mutationFn: turni.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(['turni']);
      toast.success("Turno eliminato");
    },
  });

  const handleSubmitTurno = (e) => {
    e.preventDefault();
    createTurnoMutation.mutate({ ...turnoForm, data: selectedDate });
  };

  const getCopertura = (turno) => {
    const assegnati = turno.volontari_assegnati?.length || 0;
    const richiesti = turno.volontari_richiesti || 1;
    if (assegnati >= richiesti) return { status: "completo", color: "bg-green-100 text-green-700", icon: CheckCircle };
    if (assegnati > 0) return { status: "parziale", color: "bg-yellow-100 text-yellow-700", icon: AlertTriangle };
    return { status: "scoperto", color: "bg-red-100 text-red-700", icon: AlertTriangle };
  };

  const turniScoperti = turniData?.filter(t => getCopertura(t).status === "scoperto").length || 0;
  const turniParziali = turniData?.filter(t => getCopertura(t).status === "parziale").length || 0;
  const turniCompleti = turniData?.filter(t => getCopertura(t).status === "completo").length || 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="operazioni-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Operazioni</h1>
        <p className="text-slate-500 mt-1">Gestione turni e coordinamento</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg"><Clock className="h-5 w-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold">{turniData?.length || 0}</p>
                <p className="text-xs text-slate-500">Turni Oggi</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg"><CheckCircle className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-2xl font-bold text-green-600">{turniCompleti}</p>
                <p className="text-xs text-slate-500">Completi</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-50 rounded-lg"><AlertTriangle className="h-5 w-5 text-yellow-600" /></div>
              <div>
                <p className="text-2xl font-bold text-yellow-600">{turniParziali}</p>
                <p className="text-xs text-slate-500">Parziali</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg"><AlertTriangle className="h-5 w-5 text-red-600" /></div>
              <div>
                <p className="text-2xl font-bold text-red-600">{turniScoperti}</p>
                <p className="text-xs text-slate-500">Scoperti</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="turni" className="space-y-4">
        <TabsList>
          <TabsTrigger value="turni">Gestione Turni</TabsTrigger>
          <TabsTrigger value="coordinamento">Coordinamento</TabsTrigger>
        </TabsList>

        <TabsContent value="turni" className="space-y-4">
          {/* Date selector and add button */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-slate-400" />
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-40"
              />
            </div>
            <Dialog open={turnoDialogOpen} onOpenChange={setTurnoDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0B1E3D] hover:bg-[#1E40AF]" data-testid="add-turno-btn">
                  <Plus className="h-4 w-4 mr-2" />Nuovo Turno
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Nuovo Turno</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitTurno} className="space-y-4">
                  <div>
                    <Label>Nome Turno *</Label>
                    <Input
                      value={turnoForm.nome}
                      onChange={(e) => setTurnoForm({ ...turnoForm, nome: e.target.value })}
                      placeholder="Es. Turno Cucina Mattina"
                      required
                      data-testid="input-turno-nome"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Orario Inizio</Label>
                      <Input
                        type="time"
                        value={turnoForm.orario_inizio}
                        onChange={(e) => setTurnoForm({ ...turnoForm, orario_inizio: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Orario Fine</Label>
                      <Input
                        type="time"
                        value={turnoForm.orario_fine}
                        onChange={(e) => setTurnoForm({ ...turnoForm, orario_fine: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Settore</Label>
                      <Select value={turnoForm.settore} onValueChange={(v) => setTurnoForm({ ...turnoForm, settore: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {SETTORI.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Volontari Richiesti</Label>
                      <Input
                        type="number"
                        min="1"
                        value={turnoForm.volontari_richiesti}
                        onChange={(e) => setTurnoForm({ ...turnoForm, volontari_richiesti: parseInt(e.target.value) || 1 })}
                      />
                    </div>
                  </div>
            <div className="space-y-2">
              <Label>Organizzazioni in Turno (max 2)</Label>
              <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto border rounded p-2">
                {orgList.map((org) => (
                  <label key={org.id || org._id || org.nome} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={(turnoForm.organizzazioni_turno || []).includes(org.nome)}
                      onCheckedChange={(checked) => {
                        const current = turnoForm.organizzazioni_turno || [];
                        if (checked) {
                          if (current.length < 2) setTurnoForm({ ...turnoForm, organizzazioni_turno: [...current, org.nome] });
                        } else {
                          setTurnoForm({ ...turnoForm, organizzazioni_turno: current.filter(n => n !== org.nome) });
                        }
                      }}
                    />
                    {org.nome}
                  </label>
                ))}
              </div>
              {(turnoForm.organizzazioni_turno || []).length >= 2 && <p className="text-xs text-amber-600">Massimo 2 organizzazioni selezionabili</p>}
            </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setTurnoDialogOpen(false)}>Annulla</Button>
            <Button type="submit" className="bg-[#0B1E3D] hover:bg-[#1E40AF]" data-testid="submit-turno-btn">Crea Turno</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Turni list */}
          <div className="space-y-4" data-testid="turni-list">
            {turniData?.map(turno => {
              const copertura = getCopertura(turno);
              return (
                <Card key={turno.id} data-testid={`turno-card-${turno.id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="text-center min-w-16">
                          <p className="text-lg font-bold text-[#0B1E3D]">{turno.orario_inizio}</p>
                          <p className="text-xs text-slate-400">-</p>
                          <p className="text-lg font-bold text-[#0B1E3D]">{turno.orario_fine}</p>
                        </div>
                        <div>
                          <h3 className="font-semibold">{turno.nome}</h3>
                          <Badge variant="outline" className="text-xs mt-1">{turno.settore}</Badge>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-slate-400" />
                          <span className="text-sm">
                            {turno.volontari_assegnati?.length || 0} / {turno.volontari_richiesti}
                          </span>
                        </div>
                        <Badge className={copertura.color}>
                          <copertura.icon className="h-3 w-3 mr-1" />
                          {copertura.status}
                        </Badge>
                      </div>
                    </div>

                    {/* Volontari assegnati */}
                    {turno.volontari?.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {turno.volontari.map(v => (
                          <Badge key={v.id} variant="secondary" className="text-xs">
                            {v.nome} {v.cognome}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Quick assign */}
                    {copertura.status !== "completo" && (
                      <div className="mt-4 pt-4 border-t">
                        <div className="flex items-center gap-2">
                          <Select onValueChange={(personaId) => assegnaMutation.mutate({ turnoId: turno.id, personaId })}>
                            <SelectTrigger className="w-48" data-testid={`assign-select-${turno.id}`}>
                              <SelectValue placeholder="Assegna volontario..." />
                            </SelectTrigger>
                            <SelectContent>
                              {personeData?.filter(p => !turno.volontari_assegnati?.includes(p.id)).slice(0, 20).map(p => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.nome} {p.cognome}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { if (window.confirm("Eliminare questo turno?")) deleteTurnoMutation.mutate(turno.id); }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {(!turniData || turniData.length === 0) && (
              <div className="text-center py-12 text-slate-500">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nessun turno programmato per questa data</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="coordinamento" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Coordinamento Operativo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Funzionalità in sviluppo</p>
                <p className="text-sm mt-2">Pianificazione operativa, assegnazione compiti e comunicazioni</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
